import { useMutation } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';

/**
 * `POST /area_geometries`: the fields the panel acts on, and the rest of the
 * area as the server rendered it, for a host that lists it.
 */
interface CreatedArea extends Record<string, unknown> {
  id: number;
  businessId: string;
  name: string;
}

/**
 * Carries the reasons a create was refused, so the caller shows them rather than
 * ky's generic HTTP text.
 */
class CreateRefused extends Error {
  readonly reasons: string[];

  constructor(reasons: string[]) {
    super('Create refused');
    this.reasons = reasons;
  }
}

/**
 * Turns a failed create into the messages the panel lists, matching what
 * `lib/request` reported before: the envelope's reasons when the server explained
 * itself, its status text when it didn't, and one fixed line for a dead network.
 *
 * @param {unknown} error - The thrown ky error.
 * @return {Promise<Error>} A `CreateRefused` carrying reasons, or a plain Error.
 */
const toCreateError = async (error: unknown): Promise<Error> => {
  if (!(error instanceof HTTPError)) {
    return new Error('Failed to fetch resource');
  }

  const body = (await error.response.json().catch(() => null)) as {
    errors?: { message?: string }[];
  } | null;

  const reasons = (body?.errors ?? [])
    .map((entry) => entry.message)
    .filter((message): message is string => !!message);

  if (reasons.length > 0) {
    return new CreateRefused(reasons);
  }

  return new Error(error.response.statusText || 'Could not create the area.');
};

export interface CreateArea {
  /** True while the create is in flight, to hold the button. */
  saving: boolean;
  /** Why the last attempt was refused, for the panel to list. */
  errors: string[];
  create: (payload: Record<string, unknown>) => Promise<CreatedArea | null>;
  reset: () => void;
}

/**
 * Creates an area — `POST /area_geometries` — reporting the server's refusals
 * rather than a generic failure. Retries are off: a create is not safe to repeat.
 *
 * @return {CreateArea} The submit call and its state.
 */
export const useCreateArea = (): CreateArea => {
  const { mutateAsync, isPending, error, reset } = useMutation<
    CreatedArea,
    Error,
    Record<string, unknown>
  >({
    mutationFn: async (payload) => {
      try {
        return await api
          .post('area_geometries', { json: payload })
          .json<CreatedArea>();
      } catch (requestError) {
        throw await toCreateError(requestError);
      }
    },
    retry: false,
  });

  const create = async (payload: Record<string, unknown>) => {
    try {
      return await mutateAsync(payload);
    } catch {
      // The reasons are read off `error` below; a rejection here only means the
      // panel stays open.
      return null;
    }
  };

  const reasons = (() => {
    if (!error) return [];
    if (error instanceof CreateRefused) return error.reasons;
    return [error.message || 'Could not create the area.'];
  })();

  return { saving: isPending, errors: reasons, create, reset };
};
